import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Star, MapPin, Building2, TrendingUp, Users, BadgeCheck, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

const BENEFITS = [
  { icon: Users, title: "Pre-qualified leads", text: "Reach patients who've already visualized their results and built a surgical list." },
  { icon: TrendingUp, title: "Higher conversion", text: "Consultations from AuraLift patients convert far more often — they arrive decided." },
  { icon: BadgeCheck, title: "Verified placement", text: "Featured in patient recommendations with your ratings, specialties and estimates." },
];

const CLINIC_PLANS = [
  {
    name: "Listing",
    lookup_key: "clinic_listing",
    price: "$199",
    period: "per month",
    features: ["Verified clinic profile", "Appear in patient recommendations", "Patient enquiry inbox", "Ratings & specialties displayed"],
  },
  {
    name: "Growth",
    lookup_key: "clinic_growth",
    price: "$499",
    period: "per month",
    recommended: true,
    features: ["Everything in Listing", "Featured placement in your city", "Custom treatment menu & pricing", "Cost-estimate matching", "Performance dashboard"],
  },
  {
    name: "Elite",
    lookup_key: "clinic_elite",
    price: "$999",
    period: "per month",
    features: ["Everything in Growth", "Top priority placement", "Multi-location profiles", "Dedicated partnership manager", "Co-marketing opportunities"],
  },
];

const EMPTY = { clinic_name: "", contact_name: "", email: "", phone: "", city: "", specialties: "", message: "", nda_requested: false };

export const PartnerSection = () => {
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [checkoutForm, setCheckoutForm] = useState({ clinic_name: "", email: "", nda_requested: false });
  const [checkingOut, setCheckingOut] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);

  useEffect(() => {
    api.get("/partners").then((r) => setPartners(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCo = (k) => (e) => setCheckoutForm((f) => ({ ...f, [k]: e.target.value }));

  const startCheckout = async (e) => {
    e.preventDefault();
    if (!checkoutForm.clinic_name || !checkoutForm.email) {
      toast.error("Enter your clinic name and email");
      return;
    }
    setCheckingOut(true);
    try {
      const res = await api.post("/partners/checkout", {
        lookup_key: checkoutPlan.lookup_key,
        origin_url: window.location.origin,
        clinic_name: checkoutForm.clinic_name,
        email: checkoutForm.email,
        nda_requested: checkoutForm.nda_requested,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error("Could not start checkout — please try again");
      setCheckingOut(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.clinic_name || !form.contact_name || !form.email || !form.city) {
      toast.error("Please fill in the required fields");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/partners/apply", form);
      toast.success(res.data.message);
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error("Could not submit — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-24 px-6 bg-brand-primary text-white relative overflow-hidden" id="for-clinics">
      <div className="max-w-6xl mx-auto relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm mb-5">
              <Building2 className="w-4 h-4" strokeWidth={1.5} /> For clinics & surgeons
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3 text-white">Partner with AuraLift</h2>
            <p className="text-white/70 max-w-lg leading-relaxed">
              Join the clinics already receiving decision-ready patients. Get featured in patient recommendations and turn visualizations into consultations.
            </p>
          </div>
          <Button
            data-testid="become-partner-btn"
            onClick={() => setOpen(true)}
            size="lg"
            className="rounded-full bg-brand-accent hover:bg-brand-accentHover text-white px-7 h-12 shrink-0 transition-transform hover:-translate-y-0.5"
          >
            Become a partner
          </Button>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="p-7 rounded-2xl bg-white/5 border border-white/10"
            >
              <b.icon className="w-6 h-6 text-brand-accent mb-4" strokeWidth={1.5} />
              <h3 className="font-heading text-xl mb-2 text-white">{b.title}</h3>
              <p className="text-sm text-white/70 leading-relaxed">{b.text}</p>
            </motion.div>
          ))}
        </div>

        {/* Clinic partner pricing */}
        <p className="text-sm uppercase tracking-widest text-white/50 mb-2">Partnership plans</p>
        <p className="text-white/70 mb-6 max-w-lg text-sm">Simple, flat monthly software pricing — you pay for platform features and visibility, never for individual patient outcomes. No per-booking or success fees.</p>
        <div className="grid md:grid-cols-3 gap-4 mb-8" data-testid="clinic-plans">
          {CLINIC_PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`clinic-plan-${plan.name.toLowerCase()}`}
              className={`relative flex flex-col p-7 rounded-2xl border ${
                plan.recommended
                  ? "bg-white text-brand-ink border-brand-accent shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
                  : "bg-white/5 text-white border-white/10"
              }`}
            >
              {plan.recommended && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-brand-accent text-white text-xs font-medium">Recommended</span>
              )}
              <h4 className={`font-heading text-xl mb-1 ${plan.recommended ? "text-brand-ink" : "text-white"}`}>{plan.name}</h4>
              <div className="flex items-end gap-1 mb-2">
                <span className="font-heading text-4xl">{plan.price}</span>
                <span className={`text-sm mb-1 ${plan.recommended ? "text-brand-slate" : "text-white/60"}`}>/ {plan.period}</span>
              </div>
              <div className={`inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-xs mb-5 ${plan.recommended ? "bg-brand-primary/10 text-brand-primary" : "bg-brand-accent/20 text-brand-accent"}`}>
                <TrendingUp className="w-3 h-3" /> Flat monthly · no per-booking fees
              </div>
              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.recommended ? "text-brand-primary" : "text-brand-accent"}`} />
                    <span className={plan.recommended ? "text-brand-slate" : "text-white/80"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`clinic-plan-cta-${plan.name.toLowerCase()}`}
                onClick={() => { setCheckoutPlan(plan); setCheckingOut(false); }}
                className={`w-full rounded-full h-11 transition-transform hover:-translate-y-0.5 ${
                  plan.recommended
                    ? "bg-brand-primary text-white hover:bg-brand-primaryHover"
                    : "bg-white/10 text-white hover:bg-white/20 border border-white/20"
                }`}
              >
                Subscribe &amp; get started
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Referral explainer */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-6 mb-16 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-brand-accent/20 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-brand-accent" strokeWidth={1.5} />
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            <span className="text-white font-medium">How our pricing works:</span> a flat monthly software subscription keeps your profile live and unlocks platform features — you never pay per patient, per booking, or based on any treatment outcome. Where local law permits, an optional performance/referral arrangement can be agreed separately; it is never charged by default and is not offered where medical referral-fee or fee-splitting rules restrict it.
          </p>
        </div>

        {/* Featured partners */}
        <p className="text-sm uppercase tracking-widest text-white/50 mb-6">Featured partner clinics</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="partner-list">
          {partners.map((p, i) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <Link
                to={`/clinic/${p.slug}`}
                data-testid={`partner-card-${p.slug}`}
                className="block h-full p-6 rounded-2xl bg-white/95 text-brand-ink hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-heading text-lg leading-tight">{p.name}</h4>
                  <span className="flex items-center gap-1 text-sm shrink-0 ml-2">
                    <Star className="w-3.5 h-3.5 text-brand-accent fill-brand-accent" /> {p.rating}
                  </span>
                </div>
                <p className="text-xs text-brand-slate flex items-center gap-1 mb-3">
                  <MapPin className="w-3 h-3" /> {p.location}
                </p>
                <p className="text-sm text-brand-slate mb-3">{p.tagline}</p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {p.specialties.map((s) => (
                    <span key={s} className="text-xs px-2.5 py-1 rounded-full bg-brand-sand text-brand-primary">{s}</span>
                  ))}
                </div>
                <span className="text-sm text-brand-primary font-medium">View treatments &amp; pricing →</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Clinic checkout dialog */}
      <Dialog open={ndaOpen} onOpenChange={setNdaOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[85vh] overflow-y-auto text-brand-ink" data-testid="nda-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Mutual Non-Disclosure Agreement</DialogTitle>
            <DialogDescription className="text-brand-slate">Standard mutual NDA — an optional protection for both parties.</DialogDescription>
          </DialogHeader>
          <div className="text-sm text-brand-slate leading-relaxed space-y-3 pt-2">
            <p>This Mutual Non-Disclosure Agreement is entered into between AuraLift ("AuraLift") and the clinic identified in the partnership request ("Clinic").</p>
            <p><strong className="text-brand-ink">1. Confidential Information.</strong> Each party may share non-public business, patient, pricing, technical and product information. Both parties agree to keep such information strictly confidential.</p>
            <p><strong className="text-brand-ink">2. Use.</strong> Confidential Information may be used only to evaluate and operate the partnership, and not for any competing purpose.</p>
            <p><strong className="text-brand-ink">3. Patient data.</strong> Any patient information is handled in line with applicable privacy law and shared only with consent.</p>
            <p><strong className="text-brand-ink">4. Term.</strong> Confidentiality obligations survive for three (3) years from disclosure.</p>
            <p><strong className="text-brand-ink">5. No licence.</strong> Nothing here grants ownership of the other party's intellectual property.</p>
            <p className="text-xs">Ticking "Request a mutual NDA" records your interest; AuraLift's team will send a countersignable copy before any data is exchanged.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!checkoutPlan} onOpenChange={(v) => !v && setCheckoutPlan(null)}>
        <DialogContent className="max-w-md rounded-3xl text-brand-ink" data-testid="clinic-checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Subscribe to {checkoutPlan?.name}</DialogTitle>
            <DialogDescription className="text-brand-slate">
              {checkoutPlan?.price} / month — flat software subscription, no per-booking fees. Cancel anytime.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={startCheckout} className="space-y-4 pt-2" data-testid="clinic-checkout-form">
            <div>
              <Label className="text-sm">Clinic name *</Label>
              <Input data-testid="checkout-clinic-name" value={checkoutForm.clinic_name} onChange={setCo("clinic_name")} className="mt-1 rounded-xl" placeholder="Aurora Aesthetic" />
            </div>
            <div>
              <Label className="text-sm">Billing email *</Label>
              <Input data-testid="checkout-email" type="email" value={checkoutForm.email} onChange={setCo("email")} className="mt-1 rounded-xl" placeholder="billing@clinic.com" />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" data-testid="checkout-nda" checked={checkoutForm.nda_requested} onChange={(e) => setCheckoutForm((f) => ({ ...f, nda_requested: e.target.checked }))} className="mt-0.5 accent-[#4A5D52]" />
              <span className="text-xs text-brand-slate">Request a mutual NDA before we share patient data. <button type="button" onClick={() => setNdaOpen(true)} className="text-brand-primary underline">Read NDA</button></span>
            </label>
            <Button
              data-testid="checkout-continue-btn"
              type="submit"
              disabled={checkingOut}
              className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white h-11 gap-2"
            >
              {checkingOut && <Loader2 className="w-4 h-4 animate-spin" />}
              Continue to secure payment
            </Button>
            <p className="text-xs text-brand-slate text-center">Powered by Stripe · test card 4242 4242 4242 4242</p>
          </form>
        </DialogContent>
      </Dialog>

      {/* Partner application dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto text-brand-ink" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Become a partner clinic</DialogTitle>
            <DialogDescription className="text-brand-slate">
              Tell us about your practice and our partnerships team will reach out.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2" data-testid="partner-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Clinic name *</Label>
                <Input data-testid="partner-clinic-name" value={form.clinic_name} onChange={set("clinic_name")} className="mt-1 rounded-xl" placeholder="Aurora Aesthetic" />
              </div>
              <div>
                <Label className="text-sm">Contact name *</Label>
                <Input data-testid="partner-contact-name" value={form.contact_name} onChange={set("contact_name")} className="mt-1 rounded-xl" placeholder="Dr. Jane Doe" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Email *</Label>
                <Input data-testid="partner-email" type="email" value={form.email} onChange={set("email")} className="mt-1 rounded-xl" placeholder="hello@clinic.com" />
              </div>
              <div>
                <Label className="text-sm">Phone</Label>
                <Input data-testid="partner-phone" value={form.phone} onChange={set("phone")} className="mt-1 rounded-xl" placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">City *</Label>
                <Input data-testid="partner-city" value={form.city} onChange={set("city")} className="mt-1 rounded-xl" placeholder="Los Angeles, CA" />
              </div>
              <div>
                <Label className="text-sm">Specialties</Label>
                <Input data-testid="partner-specialties" value={form.specialties} onChange={set("specialties")} className="mt-1 rounded-xl" placeholder="Rhinoplasty, BBL" />
              </div>
            </div>
            <div>
              <Label className="text-sm">Message</Label>
              <Textarea data-testid="partner-message" value={form.message} onChange={set("message")} className="mt-1 rounded-xl" placeholder="Tell us about your practice…" rows={3} />
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" data-testid="partner-nda" checked={form.nda_requested} onChange={(e) => setForm((f) => ({ ...f, nda_requested: e.target.checked }))} className="mt-0.5 accent-[#4A5D52]" />
              <span className="text-xs text-brand-slate">Request a mutual NDA before any data is shared (optional). <button type="button" onClick={() => setNdaOpen(true)} className="text-brand-primary underline">Read NDA</button></span>
            </label>
            <Button
              data-testid="partner-submit-btn"
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white h-11 gap-2"
            >
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              Submit application
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
